import { Tooltip } from 'antd';
import React, { FC } from 'react';
import styled from 'styled-components';
import { StatusFieldOption, StatusGroup, TaskType } from 'api/tasks';
import { Color } from 'components/colors';
import { useIconTypeContext } from 'contexts/icon-type';
import { StatusBlocked } from './blocked';
import { StatusCancelled } from './cancelled';
import { StatusCompleted } from './completed';
import { StatusDefault } from './default';
import { StatusInPlanning } from './in-planning';
import { StatusInProgress } from './in-progress';
import { StatusProposed } from './proposed';
import { StatusToBeStaffed } from './to-be-staffed';
import { StatusTodo } from './todo';

const Wrapper = styled.span`
  height: 20px;
  display: flex;
  align-items: center;
`;

export interface StatusIndicatorProps {
  className?: string;
  group: StatusGroup;
  progress?: number;
  type?: TaskType;
  color?: Color;
}

export const StatusIndicatorIcon: FC<StatusIndicatorProps> = ({
  group,
  progress = 0,
  type = TaskType.Task,
  color,
}) => {
  const { isExportUI: forExport } = useIconTypeContext();

  switch (group) {
    case StatusGroup.Todo:
      return <StatusTodo type={type} color={color} forExport={forExport} />;
    case StatusGroup.InProgress:
      return (
        <StatusInProgress
          type={type}
          progress={progress}
          color={color}
          forExport={forExport}
        />
      );
    case StatusGroup.Completed:
      return (
        <StatusCompleted type={type} color={color} forExport={forExport} />
      );
    case StatusGroup.Blocked:
      return <StatusBlocked type={type} color={color} forExport={forExport} />;
    case StatusGroup.Proposed:
      return <StatusProposed type={type} color={color} />;
    case StatusGroup.ToBeStaffed:
      return <StatusToBeStaffed type={type} />;
    case StatusGroup.InPlanning:
      return <StatusInPlanning type={type} />;
    case StatusGroup.Cancelled:
      return <StatusCancelled type={type} color={color} />;
    default:
      return <StatusDefault type={type} />;
  }
};

export const StatusIndicator: FC<
  Omit<StatusIndicatorProps, 'group'> & {
    showTooltip?: boolean;
    status: Pick<StatusFieldOption, 'color' | 'label' | 'group'>;
    locator?: string;
  }
> = ({ className, showTooltip = true, status, progress, type, locator }) =>
  showTooltip && status.label ? (
    <Tooltip
      title={
        <span data-cy={`tooltip.status.${status.label}`}>{status.label}</span>
      }
    >
      <Wrapper
        className={className}
        data-cy={locator || 'status-indicator-icon'}
        data-value={status.label}
      >
        <StatusIndicatorIcon
          group={status.group}
          progress={progress}
          type={type}
          color={status.color}
        />
      </Wrapper>
    </Tooltip>
  ) : (
    <Wrapper
      className={className}
      data-cy={locator || 'status-indicator-icon'}
      data-value={status.label}
    >
      <StatusIndicatorIcon
        color={status.color}
        group={status.group}
        progress={progress}
        type={type}
      />
    </Wrapper>
  );
